// TS twin of iOS WheelDetectionKit `BorderWidth` (0.7.0, ?use_ios_ui).
//
// Radial width of the masking band around one placed wheel, in real-world
// metres. Each wheel resolves its own against the size it is shown at, so a
// scene holding two sizes gets two widths.

import { bandMetres, currentWheelSize, type TCurrentWheelSize } from '@/models/current-wheel-size'

/** The band's clamp. 0.23 m clears the widest derivable band (30" on 13"). */
export const BORDER_WIDTH_MAX_M = 0.23

/** What a wheel with no known size gets — published or measured. */
export const BORDER_DEFAULT_WIDTH_M = 0.025

/** Display and geometry both round to whole millimetres. */
export const BORDER_QUANTUM_M = 0.001

/** Soft edge ADDED beyond the opaque band, never taken out of it — iOS's
 *  fixed width, and the floor for bandFadeWidth below. */
export const BORDER_FADE_WIDTH_M = 0.02

/** Share of the whole ring the fade takes (dev, 2026-10-19: "more gradient
 *  fade … make it 40%"). An Android divergence from iOS's fixed 20 mm. */
export const BAND_FADE_SHARE = 0.4

/** The fade for an opaque band of `solidM`: 40% of the ring, the band itself
 *  untouched (it is what covers the real wheel), never under 20 mm. */
export function bandFadeWidth(solidM: number): number {
  return Math.max(BORDER_FADE_WIDTH_M, (solidM * BAND_FADE_SHARE) / (1 - BAND_FADE_SHARE))
}

export interface TBorderWidth {
  readonly raw: number
  /** Whole millimetres — what the ring is built at. */
  readonly quantised: number
  /** Opaque band plus fade. */
  readonly ringWidth: number
  /** Share of the ring that is opaque. */
  readonly solidFraction: number
  readonly isOff: boolean
}

type TBorderWidthInput =
  | { readonly raw: number }
  | {
      /** The answer, or null while never asked or skipped. */
      readonly current: TCurrentWheelSize | null
      /** The shown wheel's diameter — published, else measured; null when
       *  neither exists. */
      readonly selectedInches: number | null
    }

export function borderWidth(input: TBorderWidthInput): TBorderWidth {
  return fromRaw('raw' in input ? input.raw : derivedRaw(input))
}

function derivedRaw({
  current,
  selectedInches,
}: {
  current: TCurrentWheelSize | null
  selectedInches: number | null
}): number {
  if (selectedInches == null) return BORDER_DEFAULT_WIDTH_M
  if (current == null) {
    // Unanswered parks at the wheel's own size — the one-inch buffer. Both
    // sides go through the same clamp, so a size past the range still parks.
    const parked = currentWheelSize(selectedInches)
    return bandMetres(parked, parked.inches)
  }
  return bandMetres(current, selectedInches)
}

function fromRaw(value: number): TBorderWidth {
  const raw = Math.min(Math.max(value, 0), BORDER_WIDTH_MAX_M)
  const quantised = Math.round(raw / BORDER_QUANTUM_M) * BORDER_QUANTUM_M
  const ringWidth = quantised + BORDER_FADE_WIDTH_M
  return {
    raw,
    quantised,
    ringWidth,
    solidFraction: quantised / ringWidth,
    isOff: quantised <= 0,
  }
}
